import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';

import * as echarts from 'echarts';

import { createTheme, responsiveFontSizes, ThemeProvider } from '@mui/material/styles';
import { lighten } from '@mui/system';

import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import App from './App';

import '../node_modules/react-grid-layout/css/styles.css';
import '../node_modules/react-resizable/css/styles.css';
import './react-grid-overrides.css';
import './font-override.css'

const variableFontSettings = (inkt, wght, wdth, slnt) => {
    return {
        "@supports (font-variation-settings: normal)" : {
            fontVariationSettings: `'INKT' ${inkt}, 'wght' ${wght}, 'wdth' ${wdth}, 'slnt' ${slnt}`,
        }
    }
}

const width = window.innerWidth;

let theme = createTheme({});
theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: 'rgba(231, 231, 214, 0.20)',
      dark400: '#141414',
      dark300: '#1E1E1D',
      offWhite4: 'rgba(231, 231, 214, 0.04)',
      offWhite10: 'rgba(231, 231, 214, 0.10)',
      offWhite20: 'rgba(231, 231, 214, 0.20)',
      offWhite50: 'rgba(231, 231, 214, 0.50)',
      offWhite100: 'E7E7D6',
      lightGrey20: 'rgba(174, 172, 146, 0.20)',
      lightGrey50: 'rgba(174, 172, 146, 0.50)',
      lightGrey100: '#AEAC92',
      positive: '#A2D885',
      positive20: 'rgba(162, 216, 133, 0.20)',
      negative: '#CC3E3E',
      negative20: 'rgba(204, 62, 62, 0.20)'
    },
    secondary: {
      main: '#FF522C',
      brightRed100: '#F12A34',
      kilnOrange100: '#FF522C',
      yellow100: '#FF912C',
      red100: '#FD4646',
      teal100: '#01BDB7',
      green100: '#C5E96D',
      orange100: '#FFB54A',
      neon100: '#E4EF31'
    },
    text: {
      primary: '#E7E7D6',
      secondary: 'rgba(231, 231, 214, 0.50)',
      disabled: 'rgba(231, 231, 214, 0.20)',
    },
    background: {
      default: '#141414',
      paper: '#1E1E1D',
      primarySurface: 'rgba(231, 231, 214, 0.20)',
      secondarySurface: 'rgba(231, 231, 214, 0.04)',
      tertiarySurface: 'rgba(174, 172, 146, 0.10)',
      tooltipSurface: '#464642',
      positiveSurface: 'rgba(162, 216, 133, 0.20)',
      negativeSurface: 'rgba(204, 62, 62, 0.20)'
    },
    border: {
        primary: 'rgba(231, 231, 214, 0.20)',
        active: 'rgba(231, 231, 214, 0.50)',
        secondary: 'rgba(231, 231, 214, 0.10)',
        brand:'#E7E7D6',
        gridHighlight: 'rgba(75, 48, 224, 0.50)',
        gridHighlightHover: '#4B30E0'
    }
  },
  typography: {
    fontSize: 14,
    htmlFontSize: width > 2500 ? 16 : 21.33333,
    fontFamily: [ '"area-variable-override"' ],
    h1: variableFontSettings(0, 900, 100, 0),
    h2: variableFontSettings(0, 800, 100, 0),
    h3: variableFontSettings(0, 700, 100, 0),
    h4: variableFontSettings(0, 600, 100, 0),
    h5: variableFontSettings(0, 500, 100, 0),
    h6: variableFontSettings(0, 400, 100, 0),
    body1: variableFontSettings(0, 500, 100, 0),
    body2: variableFontSettings(0, 500, 100, 0)
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        maxWidthLg: {
          [theme.breakpoints.up('lg')]: {
            maxWidth: '1700px'
          }
        },
      }
    },
    MuiInputBase: {
        border: 'none'
    },
    shape: {
        borderRadius: '15px'
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `rgba(231, 231, 214, 0.50) ${lighten('#1E1E1D', 0.051)}`,
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            backgroundColor: "rgba(231, 231, 214, 0.50)",
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "#E7E7D6",
            minHeight: 24,
            border: "3px solid #E7E7D6",
          },
          "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
            backgroundColor: "#E7E7D6",
          },
          "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
            backgroundColor: "#E7E7D6",
          },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#E7E7D6",
          },
          "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
            backgroundColor: "#E7E7D6",
          }
        }
      }
    }
  },
  mixins: {
    MuiDataGrid: {
        containerBackground: '#2B313B'
    }
  }
});
theme = responsiveFontSizes(theme);

const chartTheme = {
  "color": [
      "#01BDB7",
      "#FFB54A",
      "#C5E96D",
      "#E4EF31",
      "#71669e",
      "#FD4646",
      "#FF912C"
  ],
  "backgroundColor": "transparent",
  "textStyle": {},
  "title": {
      "textStyle": {
          "color": "#E7E7D6"
      },
      "subtextStyle": {
          "color": "rgba(231, 231, 214, 0.5)"
      }
  },
  "line": {
      "itemStyle": {
          "borderWidth": "2"
      },
      "lineStyle": {
          "width": "3"
      },
      "symbolSize": "7",
      "symbol": "circle",
      "smooth": false
  },
  "radar": {
      "itemStyle": {
          "borderWidth": "2"
      },
      "lineStyle": {
          "width": "3"
      },
      "symbolSize": "7",
      "symbol": "circle",
      "smooth": true
  },
  "bar": {
      "itemStyle": {
          "barBorderWidth": 0,
          "barBorderColor": "rgb(62,91,102)"
      }
  },
  "pie": {
      "itemStyle": {
          "borderWidth": 0,
          "borderColor": "rgb(62,91,102)"
      }
  },
  "scatter": {
      "itemStyle": {
          "borderWidth": 0,
          "borderColor": "rgb(62,91,102)"
      }
  },
  "boxplot": {
      "itemStyle": {
          "borderWidth": 0,
          "borderColor": "rgb(62,91,102)"
      }
  },
  "parallel": {
      "itemStyle": {
          "borderWidth": 0,
          "borderColor": "rgb(62,91,102)"
      }
  },
  "sankey": {
      "itemStyle": {
          "borderWidth": 0,
          "borderColor": "rgb(62,91,102)"
      }
  },
  "funnel": {
      "itemStyle": {
          "borderWidth": 0,
          "borderColor": "rgb(62,91,102)"
      }
  },
  "gauge": {
      "itemStyle": {
          "borderWidth": 0,
          "borderColor": "rgb(62,91,102)"
      }
  },
  "candlestick": {
      "itemStyle": {
          "color": "#388e3c",
          "color0": "transparent",
          "borderColor": "#388e3c",
          "borderColor0": "#d32f2f",
          "borderWidth": "1"
      }
  },
  "graph": {
      "itemStyle": {
          "borderWidth": 0,
          "borderColor": "rgb(62,91,102)"
      },
      "lineStyle": {
          "width": 1,
          "color": "rgb(105,107,112)"
      },
      "symbolSize": "7",
      "symbol": "circle",
      "smooth": true,
      "color": [
          "#8fd3e8",
          "#9b8bba",
          "#e098c7",
          "#7cb4cc",
          "#71669e",
          "#cc70af"
      ],
      "label": {
          "color": "rgba(227,227,227,0.7)"
      }
  },
  "map": {
      "itemStyle": {
          "areaColor": "#eee",
          "borderColor": "#444",
          "borderWidth": 0.5
      },
      "label": {
          "color": "#000"
      },
      "emphasis": {
          "itemStyle": {
              "areaColor": "#e098c7",
              "borderColor": "#444",
              "borderWidth": 1
          },
          "label": {
              "color": "#ffffff"
          }
      }
  },
  "geo": {
      "itemStyle": {
          "areaColor": "#eee",
          "borderColor": "#444",
          "borderWidth": 0.5
      },
      "label": {
          "color": "#000"
      },
      "emphasis": {
          "itemStyle": {
              "areaColor": "#e098c7",
              "borderColor": "#444",
              "borderWidth": 1
          },
          "label": {
              "color": "#ffffff"
          }
      }
  },
  "categoryAxis": {
      "axisLine": {
          "show": true,
          "lineStyle": {
              "color": "rgb(105,107,112)"
          }
      },
      "axisTick": {
          "show": false,
          "lineStyle": {
              "color": "#333"
          }
      },
      "axisLabel": {
          "show": true,
          "color": "rgba(231, 231, 214, 0.5)"
      },
      "splitLine": {
          "show": false,
          "lineStyle": {
              "color": [
                  "#eeeeee",
                  "#333333"
              ]
          }
      },
      "splitArea": {
          "show": true,
          "areaStyle": {
              "color": [
                  "transparent",
                  "rgba(0,0,0,0)"
              ]
          }
      }
  },
  "valueAxis": {
      "axisLine": {
          "show": true,
          "lineStyle": {
              "color": "rgb(105,107,112)"
          }
      },
      "axisTick": {
          "show": false,
          "lineStyle": {
              "color": "#333"
          }
      },
      "axisLabel": {
          "show": true,
          "color": "rgba(231, 231, 214, 0.5)"
      },
      "splitLine": {
          "show": false,
          "lineStyle": {
              "color": [
                  "#eeeeee",
                  "#333333"
              ]
          }
      },
      "splitArea": {
          "show": true,
          "areaStyle": {
              "color": [
                  "transparent",
                  "rgba(0,0,0,0)"
              ]
          }
      }
  },
  "logAxis": {
      "axisLine": {
          "show": true,
          "lineStyle": {
              "color": "rgb(105,107,112)"
          }
      },
      "axisTick": {
          "show": false,
          "lineStyle": {
              "color": "#333"
          }
      },
      "axisLabel": {
          "show": true,
          "color": "#e3e3e3"
      },
      "splitLine": {
          "show": false,
          "lineStyle": {
              "color": [
                  "#eeeeee",
                  "#333333"
              ]
          }
      },
      "splitArea": {
          "show": true,
          "areaStyle": {
              "color": [
                  "transparent",
                  "rgba(0,0,0,0)"
              ]
          }
      }
  },
  "timeAxis": {
      "axisLine": {
          "show": true,
          "lineStyle": {
              "color": "rgb(105,107,112)"
          }
      },
      "axisTick": {
          "show": false,
          "lineStyle": {
              "color": "#333"
          }
      },
      "axisLabel": {
          "show": true,
          "color": "#e3e3e3"
      },
      "splitLine": {
          "show": false,
          "lineStyle": {
              "color": [
                  "#eeeeee",
                  "#333333"
              ]
          }
      },
      "splitArea": {
          "show": true,
          "areaStyle": {
              "color": [
                  "transparent",
                  "rgba(0,0,0,0)"
              ]
          }
      }
  },
  "toolbox": {
      "iconStyle": {
          "borderColor": "rgb(105,107,112)"
      },
      "emphasis": {
          "iconStyle": {
              "borderColor": "rgb(171,174,179)"
          }
      }
  },
  "legend": {
      "textStyle": {
          "color": "#E7E7D6"
      }
  },
  "tooltip": {
      "axisPointer": {
          "lineStyle": {
              "color": "rgb(105,107,112)",
              "width": 1
          },
          "crossStyle": {
              "color": "rgb(105,107,112)",
              "width": 1
          }
      }
  },
  "timeline": {
      "lineStyle": {
          "color": "#E7E7D6",
          "width": 1
      },
      "itemStyle": {
          "color": "#E7E7D6",
          "borderWidth": 1
      },
      "controlStyle": {
          "color": "#8fd3e8",
          "borderColor": "#rgba(231, 231, 214, 0.20)",
          "borderWidth": 0.5
      },
      "checkpointStyle": {
          "color": "#8fd3e8",
          "borderColor": "#8a7ca8"
      },
      "label": {
          "color": "#E7E7D6"
      },
      "emphasis": {
          "itemStyle": {
              "color": "#8fd3e8"
          },
          "controlStyle": {
              "color": "#8fd3e8",
              "borderColor": "#8fd3e8",
              "borderWidth": 0.5
          },
          "label": {
              "color": "#E7E7D6"
          }
      }
  },
  "visualMap": {
      "color": [
          "#8a7ca8",
          "#e098c7",
          "#cceffa"
      ]
  },
  "dataZoom": {
      "backgroundColor": "rgba(0,0,0,0)",
      "dataBackgroundColor": "rgba(255,255,255,0.3)",
      "fillerColor": "rgba(167,183,204,0.4)",
      "handleColor": "#a7b7cc",
      "handleSize": "100%",
      "textStyle": {
          "color": "rgba(231, 231, 214, 0.50)"
      }
  },
  "markPoint": {
      "label": {
          "color": "rgba(227,227,227,0.7)"
      },
      "emphasis": {
          "label": {
              "color": "rgba(227,227,227,0.7)"
          }
      }
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
echarts.registerTheme('kiln-dark', chartTheme);


pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
).toString();

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
        <Auth0Provider
            domain={auth0Domain}
            clientId={auth0ClientId}
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience: 'https://api.kilnanalytics.com',
                prompt: 'consent',
                scope: "openid profile email read:datasources write:datasources read:kiln-data read:workspaces write:workspaces"
        }}>
            <App />
        </Auth0Provider>
    </ThemeProvider>
  </React.StrictMode>
);

if (module.hot) module.hot.accept();
